import type { DeliveryCompany, DeliveryEntry } from "./use-delivery-companies";

// Integer minor units keep decimal-string ledger sums exact.
export function minor(value: string): number {
  const match = /^(-?)(\d+)(?:\.(\d{0,2}))?$/.exec(value);
  if (!match) throw new Error("Invalid delivery ledger amount");
  return (match[1] ? -1 : 1) * (Number(match[2]) * 100 + Number((match[3] || "").padEnd(2, "0")));
}
export const delta = (e: DeliveryEntry) =>
  e.k === "charge" ? minor(e.amt) : e.k === "received" ? -minor(e.amt) : e.settle ? minor(e.amt) : 0;
export const balance = (c: DeliveryCompany, branch = "") =>
  c.entries.filter(e => !branch || e.br === branch).reduce((sum, e) => sum + delta(e), 0);
export const uses = (c: DeliveryCompany, branch: string) => c.br === "all" || c.br.includes(branch);
export const fmt = (cents: number) => (Math.abs(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const signed = (cents: number) => (cents > 0 ? "+" : cents < 0 ? "−" : "") + fmt(cents);
export function companyStats(c: DeliveryCompany, branch: string, today: string) {
  const entries = c.entries.filter(e => !branch || e.br === branch);
  const month = today.slice(0, 7);
  const monthly = entries.filter(e => e.d.slice(0, 7) === month);
  const lastRec = entries.filter(e => e.k === "received").map(e => e.d).sort().pop() || null;
  const ds = lastRec ? Math.round((Date.parse(today.slice(0, 10)) - Date.parse(lastRec.slice(0, 10))) / 86400000) : null;
  return {
    c, v: balance(c, branch), n: entries.length, lastRec, ds,
    ch: monthly.filter(e => e.k === "charge").reduce((s, e) => s + minor(e.amt), 0),
    rc: monthly.filter(e => e.k === "received").reduce((s, e) => s + minor(e.amt), 0),
  };
}
export type CompanyStats = ReturnType<typeof companyStats>;
export const late = (r: CompanyStats) => r.v > 0 && (r.ds === null || r.ds > 30);
export function statement(c: DeliveryCompany, branch: string) {
  // Stable API order breaks ties when opaque IDs have no chronological meaning.
  let running = 0;
  return c.entries.map((e, index) => ({ ...e, index }))
    .sort((a, b) => a.d.localeCompare(b.d) || a.index - b.index)
    .filter(e => !branch || e.br === branch)
    .map(e => ({ ...e, after: running += delta(e) })).reverse();
}
